import React, { useEffect, useRef, useState } from "react";
import { C, font } from "../lib/styles.js";
import {
  IDLE_CHOICES, SHARE_EVERY_MS, idleState, lastUseOf, readIdleMinutes, readLastUse, writeIdleMinutes, writeLastUse,
} from "../lib/idleSignOut.js";

/*
  Idle sign-out on a shared device (docs/ops/password-signin.md). Off unless
  this device's setting is on. Mounted by App.jsx while someone is signed in,
  the second-display window included. Any tap, click, key, scroll or touch
  counts as use, in this tab or any other tab of the device (the last use is
  shared through the browser's storage). In the last minute a banner says
  what is about to happen; any use keeps the session.
*/
const ACTIVITY = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];

export function useIdleMinutes() {
  const storage = typeof window !== "undefined" ? safeLocalStorage() : null;
  const [minutes, setMinutes] = useState(() => readIdleMinutes(storage));
  // Another tab changing the setting on this device.
  useEffect(() => {
    const onStorage = () => setMinutes(readIdleMinutes(safeLocalStorage()));
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const save = (n) => setMinutes(writeIdleMinutes(safeLocalStorage(), n));
  return [minutes, save];
}

function safeLocalStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export default function IdleSignOut({ minutes, onExpire }) {
  const last = useRef(Date.now());
  const [view, setView] = useState({ state: "active" });
  const expired = useRef(false);

  useEffect(() => {
    if (!minutes) return undefined;
    const storage = safeLocalStorage();
    last.current = Date.now();
    let shared = 0;
    expired.current = false;
    const share = (now) => {
      if (now - shared < SHARE_EVERY_MS) return;
      shared = now;
      writeLastUse(storage, now);
    };
    share(last.current);
    const used = () => {
      last.current = Date.now();
      share(last.current);
      setView((v) => (v.state === "warning" ? { state: "active" } : v));
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const check = () => {
      const now = Date.now();
      const s = idleState(minutes, lastUseOf(last.current, readLastUse(storage), now), now);
      if (s.state === "expired") {
        if (!expired.current) {
          expired.current = true;
          onExpire();
        }
        return;
      }
      setView((v) => (v.state === s.state && v.secondsLeft === s.secondsLeft ? v : s));
    };
    for (const type of ACTIVITY) window.addEventListener(type, used, { passive: true, capture: true });
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(check, 1000);
    return () => {
      for (const type of ACTIVITY) window.removeEventListener(type, used, { capture: true });
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [minutes, onExpire]);

  if (!minutes || view.state !== "warning") return null;
  return (
    <div role="alert" className="idle-warning" style={styles.banner}>
      Nobody has used this device for a while. Signing out in {view.secondsLeft} seconds. Tap anywhere to stay
      signed in.
    </div>
  );
}

// The admin's control on the home page: this device's setting.
export function IdleSetting({ minutes, onChange }) {
  return (
    <label className="idle-setting" style={styles.setting}>
      <span>Sign this device out after</span>
      <select value={minutes} onChange={(e) => onChange(Number(e.target.value))} style={styles.select} aria-label="Sign this device out after">
        {IDLE_CHOICES.map((n) => (
          <option key={n} value={n}>{n === 0 ? "never (off)" : `${n} minutes without use`}</option>
        ))}
      </select>
    </label>
  );
}

const styles = {
  banner: {
    position: "fixed",
    left: 12,
    right: 12,
    bottom: 12,
    zIndex: 1000,
    padding: "14px 16px",
    borderRadius: 8,
    background: C.text,
    color: "#FFF",
    fontFamily: font,
    fontSize: 15,
    lineHeight: 1.4,
    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
  },
  setting: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "center",
    marginTop: 16,
    fontSize: 13,
    color: C.muted,
    fontFamily: font,
  },
  select: {
    fontSize: 16,
    minHeight: 44,
    padding: "0 10px",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    background: C.surface,
    color: C.text,
    fontFamily: font,
  },
};
